import { Delete } from '@mui/icons-material';
import { Autocomplete, Grid, List, ListItem, ListItemIcon, TextField } from '@mui/material';
import { grey } from '@mui/material/colors';
import { debounce, keyBy } from 'lodash';
import { useMemo, useRef } from 'react';

import { type ManagersType } from '../../types';

import { ProjectUserTypeEnum } from '@/graphql/generated/graphql';
import { SuperThin } from '@/theme';
import { titleizeCamelCase } from '@/utils/helperFunctions';

interface ProjectManagersProps {
  allManagers: Array<{ id: string; name?: string | null }>;
  managers: ManagersType[];
  setManagers: React.Dispatch<React.SetStateAction<ManagersType[]>>;
}

const ProjectManagers: React.FC<ProjectManagersProps> = ({ allManagers = [], managers, setManagers }) => {
  const managerLookup = useMemo(() => keyBy(allManagers, 'id'), [allManagers]);

  const selectedResourceIds = useMemo(
    () => new Set(managers.map((m) => m.employmentId || m.companyResourceId).filter(Boolean)),
    [managers],
  );

  const addManager = (val: { id: string; name?: string | null } | null | undefined) => {
    if (!val) return;
    const newManager: ManagersType = {
      id: `temp-${Date.now()}`, // Unique temp ID
      title: '',
      userType: ProjectUserTypeEnum.User,
      employmentId: val.id,
      companyResourceId: val.id,
    };
    setManagers((prev) => [...prev, newManager]);
  };

  const debouncedUpdate = useRef(
    debounce((managerId, value) => {
      setManagers((prev) => prev.map((m) => (m.id === managerId ? { ...m, title: value } : m)));
    }, 500),
  ).current;

  const removeManager = (managerId: string) => {
    setManagers((prev) => prev.filter((m) => m.id !== managerId));
  };

  return (
    <>
      <Autocomplete
        options={allManagers.filter((opt) => !selectedResourceIds.has(opt.id))}
        getOptionLabel={(option) => option.name || ''}
        value={null}
        onChange={(_e, val) => addManager(val)}
        renderInput={(params) => <TextField {...params} label="Select Manager" variant="outlined" />}
        sx={{ mb: 2, minWidth: '30%' }}
        blurOnSelect
        clearOnBlur
      />

      <List dense sx={{ maxHeight: 300, overflow: 'auto', counterReset: 'manager-list' }}>
        {managers.map((manager) => (
          <ListItem
            key={manager.id} // Stable ID
            sx={{
              pl: '30px',
              position: 'relative',
              '&::before': {
                content: 'counter(manager-list) "."',
                counterIncrement: 'manager-list',
                position: 'absolute',
                left: 0,
                fontWeight: 'bold',
                color: 'text.secondary',
              },
            }}
          >
            <Grid
              sx={{
                border: `1px solid ${grey[400]}`,
                borderRadius: '5px',
                p: 1,
                flex: 1, // Let it grow
                minWidth: '150px',
              }}
            >
              {titleizeCamelCase(
                managerLookup[manager.employmentId || manager.companyResourceId || '']?.name || 'Unknown',
              )}
            </Grid>

            <TextField
              defaultValue={manager.title}
              label="Role/Designation"
              sx={{ ...SuperThin, ml: 2, width: 300 }}
              onChange={(e) => debouncedUpdate(manager.id, e.target.value)}
            />

            <ListItemIcon sx={{ justifyContent: 'flex-end', minWidth: '40px' }}>
              <Delete onClick={() => removeManager(manager.id)} color="error" sx={{ cursor: 'pointer' }} />
            </ListItemIcon>
          </ListItem>
        ))}
      </List>
    </>
  );
};

export default ProjectManagers;
